import { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Alert,
  Paper,
  Badge,
  Input,
  Radio,
  SegmentedControl,
  TextInput,
  Textarea,
  Timeline,
  Divider,
  ThemeIcon,
  ScrollArea,
  Box,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconPhone,
  IconMessageCircle,
  IconPrinter,
  IconPlayerPlay,
  IconCheck,
  IconX,
  IconClock,
  IconUser,
  IconPhoneCall,
  IconMessage,
  IconSend,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";

const CONTACT_METHOD_OPTIONS = [
  { value: "voice", label: "Phone call" },
  { value: "sms", label: "SMS" },
  { value: "fax", label: "Fax" },
];

interface MalignancyInfo {
  specimenNumber: string;
  specimenLabel: string;
  diagnosisText: string;
  categoryName: string;
}

interface NotificationAttempt {
  id: string;
  channel: "voice" | "sms" | "fax";
  recipient: string;
  status: string;
  attemptedAt: string;
  completedAt?: string;
  durationSeconds?: number;
  errorMessage?: string;
}

interface PhysicianContact {
  id: string;
  firstName: string;
  lastName: string;
  phone?: string;
  fax?: string;
}

interface CancerNotification {
  id: string;
  status: string;
  notificationMethod?: string;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  notes?: string;
}

interface MalignancyNotificationModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  caseNumber: string;
  malignancies: MalignancyInfo[];
  physician: PhysicianContact | null;
  notification: CancerNotification | null;
  attempts: NotificationAttempt[];
  onInitiateCall: (
    phoneNumber?: string
  ) => Promise<{ success: boolean; error?: string }>;
  onSendSms: (
    phoneNumber?: string,
    message?: string
  ) => Promise<{ success: boolean; error?: string }>;
  onSendFax: (
    faxNumber?: string
  ) => Promise<{ success: boolean; error?: string }>;
  onDocumentOutcome: (data: {
    outcome: string;
    spokeWith?: string;
    notes?: string;
    notificationMethod: string;
  }) => Promise<{ success: boolean; error?: string }>;
  onGetRecordingUrl: (attemptId: string) => Promise<string | null>;
  onRefresh: () => void;
  isLoading?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function MalignancyNotificationModal({
  opened,
  onClose,
  caseId,
  caseNumber,
  malignancies,
  physician,
  notification,
  attempts,
  onInitiateCall,
  onSendSms,
  onSendFax,
  onDocumentOutcome,
  onGetRecordingUrl,
  onRefresh,
  isLoading = false,
}: MalignancyNotificationModalProps) {
  // Note: caseId, caseNumber, notification, and isLoading are part of the interface
  // but not currently used in the component. They may be needed for future enhancements.
  void caseId;
  void caseNumber;
  void notification;
  void isLoading;
  const [outcome, setOutcome] = useState<string | null>(null);
  const [spokeWith, setSpokeWith] = useState("");
  const [spokeWithError, setSpokeWithError] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCallingPhone, setIsCallingPhone] = useState(false);
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [isSendingFax, setIsSendingFax] = useState(false);
  const [lastUsedChannel, setLastUsedChannel] = useState<
    "voice" | "sms" | "fax" | null
  >(null);
  const [playingRecordingId, setPlayingRecordingId] = useState<string | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const spokeWithRef = useRef<HTMLInputElement>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      setOutcome(null);
      setSpokeWith("");
      setSpokeWithError(null);
      setNotes("");
      setError(null);
      setLastUsedChannel(null);
    }
  }, [opened]);

  // Auto-fill spokeWith when "spoke_with_physician" is selected
  useEffect(() => {
    if (outcome === "spoke_with_physician" && physician) {
      const physicianName =
        `Dr. ${physician.firstName} ${physician.lastName}`.trim();
      setSpokeWith(physicianName);
      setSpokeWithError(null);
    } else if (outcome === "spoke_with_staff") {
      // Clear for staff option so user can enter the staff name
      setSpokeWith("");
    }
  }, [outcome, physician]);

  // Auto-refresh attempts every 5 seconds when there are pending attempts
  useEffect(() => {
    if (!opened) return;

    const hasPendingAttempts = attempts.some(
      (a) => a.status === "pending" || a.status === "sent"
    );

    if (hasPendingAttempts) {
      const interval = setInterval(onRefresh, 5000);
      return () => clearInterval(interval);
    }
  }, [opened, attempts, onRefresh]);

  // Derive active channel: prefer the button the user just clicked, then fall
  // back to the most-recent attempt channel so the section is pre-set when the
  // modal reopens with existing attempts.
  const activeChannel: "voice" | "sms" | "fax" =
    lastUsedChannel ??
    (attempts.length > 0 ? attempts[attempts.length - 1].channel : "voice");

  const outcomeOptions: { value: string; label: string }[] = (() => {
    switch (activeChannel) {
      case "sms":
        return [
          { value: "sms_delivered", label: "SMS delivered successfully" },
          {
            value: "sms_no_response",
            label: "SMS delivered — no response received",
          },
          {
            value: "unable_to_reach",
            label: "Unable to reach after 3+ attempts",
          },
        ];
      case "fax":
        return [
          {
            value: "fax_confirmed",
            label: "Fax sent with delivery confirmation",
          },
          { value: "fax_failed", label: "Fax failed — delivery not confirmed" },
          {
            value: "unable_to_reach",
            label: "Unable to reach after 3+ attempts",
          },
        ];
      default:
        // voice
        return [
          {
            value: "spoke_with_physician",
            label: "Spoke directly with physician",
          },
          {
            value: "spoke_with_staff",
            label: "Spoke with nurse/staff — message relayed",
          },
          { value: "left_voicemail", label: "Left voicemail" },
          {
            value: "unable_to_reach",
            label: "Unable to reach after 3+ attempts",
          },
        ];
    }
  })();

  const outcomeLabel =
    activeChannel === "sms"
      ? "SMS Outcome"
      : activeChannel === "fax"
      ? "Fax Outcome"
      : "Call Outcome";

  const handleInitiateCall = async () => {
    setIsCallingPhone(true);
    setLastUsedChannel("voice");
    setOutcome(null);
    setError(null);
    try {
      const result = await onInitiateCall();
      if (!result.success) {
        setError(result.error || "Failed to initiate call");
      } else {
        notifications.show({
          title: "Call Initiated",
          message: "Connecting to physician...",
          color: "blue",
        });
        onRefresh();
      }
    } catch (err: any) {
      setError(err.message || "Failed to initiate call");
    } finally {
      setIsCallingPhone(false);
    }
  };

  const handleSendSms = async () => {
    setIsSendingSms(true);
    setLastUsedChannel("sms");
    setOutcome(null);
    setError(null);
    try {
      const result = await onSendSms();
      if (!result.success) {
        setError(result.error || "Failed to send SMS");
      } else {
        notifications.show({
          title: "SMS Sent",
          message: "Notification sent to physician",
          color: "green",
        });
        onRefresh();
      }
    } catch (err: any) {
      setError(err.message || "Failed to send SMS");
    } finally {
      setIsSendingSms(false);
    }
  };

  const handleSendFax = async () => {
    setIsSendingFax(true);
    setLastUsedChannel("fax");
    setOutcome(null);
    setError(null);
    try {
      const result = await onSendFax();
      if (!result.success) {
        setError(result.error || "Failed to send fax");
      } else {
        notifications.show({
          title: "Fax Sent",
          message: "Report faxed to physician",
          color: "green",
        });
        onRefresh();
      }
    } catch (err: any) {
      setError(err.message || "Failed to send fax");
    } finally {
      setIsSendingFax(false);
    }
  };

  const handlePlayRecording = async (attemptId: string) => {
    setPlayingRecordingId(attemptId);
    try {
      const url = await onGetRecordingUrl(attemptId);
      if (url) {
        // Open recording in new tab or play audio
        window.open(url, "_blank");
      } else {
        notifications.show({
          title: "Recording Not Available",
          message: "The recording is not yet available or has been deleted",
          color: "yellow",
        });
      }
    } catch (err) {
      notifications.show({
        title: "Error",
        message: "Failed to get recording",
        color: "red",
      });
    } finally {
      setPlayingRecordingId(null);
    }
  };

  const handleContactMethodChange = useCallback((value: string) => {
    setLastUsedChannel(value as "voice" | "sms" | "fax");
    setOutcome(null);
  }, []);

  const handleDocumentAndSignOut = async () => {
    setError(null);
    setSpokeWithError(null);

    // Validate outcome selection
    if (!outcome) {
      notifications.show({
        title: "Selection Required",
        message: "Please select an outcome before proceeding",
        color: "orange",
      });
      return;
    }

    // Validate spokeWith field if required
    const requiresSpokeWith =
      outcome === "spoke_with_physician" || outcome === "spoke_with_staff";
    if (requiresSpokeWith && !spokeWith.trim()) {
      setSpokeWithError("This field is required");
      spokeWithRef.current?.focus();
      spokeWithRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    // A pathologist who reached the physician on their own line has already done the thing that
    // matters, and the practice numbers held here are often not reachable anyway. The outcome
    // recorded below is the record of that contact; no attempt has to be placed through the system.

    // Determine notification method from active channel
    let notificationMethod: string;
    if (activeChannel === "fax") {
      notificationMethod = "fax";
    } else if (activeChannel === "sms") {
      notificationMethod = "sms";
    } else {
      notificationMethod = "phone";
    }

    setIsSubmitting(true);
    try {
      const result = await onDocumentOutcome({
        outcome,
        spokeWith: spokeWith.trim() || undefined,
        notes: notes.trim() || undefined,
        notificationMethod,
      });

      if (!result.success) {
        setError(result.error || "Failed to document outcome");
        return;
      }

      notifications.show({
        title: "Notification Documented",
        message:
          "Malignancy notification has been documented. Proceeding to sign-off...",
        color: "green",
      });

      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to document outcome");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return "";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getAttemptStatusColor = (status: string) => {
    switch (status) {
      case "delivered":
        return "green";
      case "sent":
        return "blue";
      case "pending":
        return "yellow";
      case "failed":
      case "no_answer":
        return "red";
      default:
        return "gray";
    }
  };

  const getAttemptStatusIcon = (status: string) => {
    switch (status) {
      case "delivered":
        return <IconCheck size={12} />;
      case "sent":
        return <IconSend size={12} />;
      case "pending":
        return <IconClock size={12} />;
      case "failed":
      case "no_answer":
        return <IconX size={12} />;
      default:
        return null;
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case "voice":
        return <IconPhoneCall size={16} />;
      case "sms":
        return <IconMessage size={16} />;
      case "fax":
        return <IconPrinter size={16} />;
      default:
        return null;
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <ThemeIcon color="orange" variant="light" size="md">
            <IconAlertTriangle size={18} />
          </ThemeIcon>
          <span>Malignancy Notification Required</span>
        </Group>
      }
      size="lg"
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <ScrollArea.Autosize mah="60vh" offsetScrollbars>
        <Stack gap="md" pr="xs">
          {/* Malignancy Info */}
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="This case contains a malignancy diagnosis"
            color="orange"
          >
            <Stack gap="xs">
              {malignancies.map((m, idx) => (
                <Text key={idx} size="sm">
                  <strong>{m.specimenLabel}:</strong> {m.categoryName}
                </Text>
              ))}
            </Stack>
            <Text size="sm" mt="xs" fw={500}>
              Record how the physician was notified before signing out. If you
              already reached them outside the system, choose the method and
              outcome below — you do not have to place a call, SMS, or fax from
              here.
            </Text>
          </Alert>

          {error && (
            <Alert icon={<IconX size={16} />} title="Error" color="red">
              {error}
            </Alert>
          )}

          {/* Physician Contact */}
          <Paper p="md" withBorder>
            <Text fw={600} mb="xs">
              Physician Contact
            </Text>
            {physician ? (
              <Stack gap="xs">
                <Text size="sm">
                  <IconUser
                    size={14}
                    style={{ verticalAlign: "middle", marginRight: 4 }}
                  />
                  Dr. {physician.firstName} {physician.lastName}
                </Text>
                <Group gap="xl">
                  {physician.phone && (
                    <Text size="sm">
                      <IconPhone
                        size={14}
                        style={{ verticalAlign: "middle", marginRight: 4 }}
                      />
                      {physician.phone}
                    </Text>
                  )}
                  {physician.fax && (
                    <Text size="sm">
                      <IconPrinter
                        size={14}
                        style={{ verticalAlign: "middle", marginRight: 4 }}
                      />
                      {physician.fax}
                    </Text>
                  )}
                </Group>

                {/* Contact Method Buttons */}
                <Text size="xs" c="dimmed" mt="sm">
                  Optional — send an attempt from here. These numbers usually
                  reach the practice rather than the physician directly.
                </Text>
                <Group gap="xs">
                  <Button
                    leftSection={<IconPhone size={16} />}
                    onClick={handleInitiateCall}
                    loading={isCallingPhone}
                    disabled={!physician.phone || isSendingSms || isSendingFax}
                    variant="filled"
                    color="blue"
                  >
                    Call
                  </Button>
                  <Button
                    leftSection={<IconMessageCircle size={16} />}
                    onClick={handleSendSms}
                    loading={isSendingSms}
                    disabled={
                      !physician.phone || isCallingPhone || isSendingFax
                    }
                    variant="filled"
                    color="teal"
                  >
                    SMS
                  </Button>
                  <Button
                    leftSection={<IconPrinter size={16} />}
                    onClick={handleSendFax}
                    loading={isSendingFax}
                    disabled={!physician.fax || isCallingPhone || isSendingSms}
                    variant="filled"
                    color="grape"
                  >
                    Fax Report
                  </Button>
                </Group>
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                No physician assigned to this case
              </Text>
            )}
          </Paper>

          {/* Communication History */}
          {attempts.length > 0 && (
            <Paper p="md" withBorder>
              <Text fw={600} mb="sm">
                Communication History
              </Text>
              <Timeline
                active={attempts.length - 1}
                bulletSize={24}
                lineWidth={2}
              >
                {attempts.map((attempt) => (
                  <Timeline.Item
                    key={attempt.id}
                    bullet={getChannelIcon(attempt.channel)}
                    title={
                      <Group gap="xs">
                        <Text size="sm" fw={500}>
                          {attempt.channel === "voice"
                            ? "Call"
                            : attempt.channel === "sms"
                            ? "SMS"
                            : "Fax"}
                        </Text>
                        <Badge
                          size="xs"
                          color={getAttemptStatusColor(attempt.status)}
                          leftSection={getAttemptStatusIcon(attempt.status)}
                        >
                          {attempt.status === "no_answer"
                            ? "No Answer"
                            : attempt.status}
                        </Badge>
                        {attempt.durationSeconds && (
                          <Text size="xs" c="dimmed">
                            ({formatDuration(attempt.durationSeconds)})
                          </Text>
                        )}
                      </Group>
                    }
                  >
                    <Text size="xs" c="dimmed">
                      {new Date(attempt.attemptedAt).toLocaleString("en-US", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}{" "}
                      - {attempt.recipient}
                    </Text>
                    {attempt.channel === "voice" &&
                      attempt.status === "delivered" && (
                        <Button
                          size="xs"
                          variant="subtle"
                          leftSection={<IconPlayerPlay size={12} />}
                          onClick={() => handlePlayRecording(attempt.id)}
                          loading={playingRecordingId === attempt.id}
                          mt="xs"
                        >
                          Play Recording
                        </Button>
                      )}
                    {attempt.errorMessage && (
                      <Text size="xs" c="red" mt="xs">
                        Error: {attempt.errorMessage}
                      </Text>
                    )}
                  </Timeline.Item>
                ))}
              </Timeline>
            </Paper>
          )}

          <Divider />

          {/* Outcome Documentation */}
          <Input.Wrapper
            label="How was the physician contacted?"
            description="Covers contact made outside the system as well as attempts sent from here."
          >
            <SegmentedControl
              fullWidth
              mt="xs"
              value={activeChannel}
              onChange={handleContactMethodChange}
              data={CONTACT_METHOD_OPTIONS}
              disabled={isSubmitting}
            />
          </Input.Wrapper>

          <Radio.Group
            value={outcome || ""}
            onChange={(value) => setOutcome(value)}
            label={outcomeLabel}
            required
          >
            <Stack gap="xs" mt="xs">
              {outcomeOptions.map((opt) => (
                <Radio key={opt.value} value={opt.value} label={opt.label} />
              ))}
            </Stack>
          </Radio.Group>

          {(outcome === "spoke_with_physician" ||
            outcome === "spoke_with_staff") && (
            <TextInput
              ref={spokeWithRef}
              label="Spoke With"
              placeholder="Name of person contacted"
              value={spokeWith}
              onChange={(e) => {
                setSpokeWith(e.currentTarget.value);
                if (spokeWithError) setSpokeWithError(null);
              }}
              required
              error={spokeWithError}
            />
          )}

          <Textarea
            label="Notes"
            placeholder="Additional notes about the notification..."
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            minRows={2}
            maxRows={4}
            autosize
          />
        </Stack>
      </ScrollArea.Autosize>

      {/* Fixed Footer */}
      <Box
        pt="md"
        style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
      >
        <Group justify="space-between">
          <Button variant="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDocumentAndSignOut}
            loading={isSubmitting}
            color="green"
          >
            Document & Sign Out
          </Button>
        </Group>
      </Box>
    </Modal>
  );
}
